import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'react-use';
import MultiSelectInline from '__components/Inputs/inline/MultiSelect';
import ClearSelectButton from '__components/Inputs/Select/ClearButton';
import { ApplySelectOverlayButton } from '__components/Inputs/SelectOverlay';
import MultiSelect from '__components/Inputs/MultiSelect';
import { permissionGroupsQuery } from 'query/permission-group';
import { namedToOption, optionToNamed } from 'services/helpers';
import { snakeToCamelCaseKeys } from '__services/helpers';
import { DEFAULT_INPUT_DELAY } from 'utility/config';

type Action = 'input-change' | 'input-blur' | 'menu-close' | 'set-value';

type PermissionGroup = {
  id: string;
  name: string;
};

type Option = {
  label: string;
  value: string;
};

type TeamMemberPermissionGroupsProps = {
  value: PermissionGroup[];
  onSubmit(value: PermissionGroup[]): void;
};

const TeamMemberPermissionGroups = (props: TeamMemberPermissionGroupsProps) => {
  const { value: valueProp = [], onSubmit, ...rest } = props;

  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const value = useMemo(() => {
    return valueProp.map(namedToOption);
  }, [valueProp]);

  const { data: options = [] } = useQuery(
    permissionGroupsQuery({
      ...(debouncedSearch && {
        params: { search: debouncedSearch },
      }),
      placeholderData: keepPreviousData,
      staleTime: Infinity,
      select: (data) => {
        const camel = snakeToCamelCaseKeys(data);
        return camel.map((pg) => {
          return { value: pg.id, label: pg.name };
        });
      },
    })
  );

  useDebounce(
    () => {
      setDebouncedSearch(search);
    },
    DEFAULT_INPUT_DELAY,
    [search]
  );

  return (
    <MultiSelectInline
      onSubmit={(val: Option[]) => {
        onSubmit(val.map(optionToNamed));
      }}
      {...rest}
    >
      <MultiSelect
        value={value}
        inputValue={search}
        placeholder={t('Find Permission Groups')}
        options={options}
        menuLeftButton={<ClearSelectButton />}
        menuRightButton={<ApplySelectOverlayButton />}
        noOptionsMessage={() => {
          return t('No Options');
        }}
        onInputChange={(v: string, meta: { action: Action }) => {
          if (meta.action === 'input-change') {
            setSearch(v);
          }

          if (meta.action === 'menu-close') {
            setSearch('');
          }
        }}
      />
    </MultiSelectInline>
  );
};

export default TeamMemberPermissionGroups;
